import { logStartUsage, logStartGreyError } from 'owa-analytics-start';
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';
import { getSerializationIssue } from './serialization/getSerializationIssue';
import { serialize } from './serialization/serialize';
import type { SerializationIssue } from './types/SerializationTypes';
import { traverseObjectDeep } from './utils/traverseObjectDeep';
import { getSerializationError } from './serialization/getSerializationError';
import type { ExtendedWorkerOptions } from 'owa-analytics-types';

export function postMessageWrapper(
    _this: MessagePort | Worker,
    pPostMessage: MessagePort['postMessage'],
    message: any,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    options?: any,
    workerName?: ExtendedWorkerOptions['name']
): void {
    const STACK_SIZE_LIMIT = Error.stackTraceLimit;
    // If available, increase the stack trace limit from 10 to 50 se we can see the repeating pattern
    if (Error.stackTraceLimit) {
        Error.stackTraceLimit = 50;
    }

    try {
        pPostMessage.call(_this, message, options);
    } catch (caught) {
        // postMessage clone failures are surfaced as DOMException in some browsers, which
        // does not always extend Error. Treat both as recoverable so the DataCloneError
        // retry path (used by AnalyticsWorker) keeps running.
        const error: Error | undefined =
            caught instanceof Error ||
            (typeof DOMException !== 'undefined' && caught instanceof DOMException)
                ? (caught as Error)
                : undefined;
        if (isDataCloneError(error) && message) {
            const allSerializationIssues = findAllSerializationErrors(message);

            const { retried, retriedFailed } = retryForAnalytics(
                error,
                _this,
                pPostMessage,
                message,
                options,
                workerName
            );

            const count = allSerializationIssues.length;
            const issue = count > 0 ? allSerializationIssues[0] : null;

            const errorWithDetails = getSerializationError(
                error,
                issue,
                retried,
                retriedFailed,
                count
            );

            logStartGreyError(
                'SerializationError_Cloning',
                /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
                 * Justification: Worker infrastructure code has no mailbox context - runs before user data is established. */
                INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
                errorWithDetails,
                {
                    diagnostic: errorWithDetails?.diagnosticInfo,
                }
            );
        } else {
            // Log other types of errors with postMessage
            const errorWithDetails = getSerializationError(error, null, false, false);

            logStartGreyError(
                'SerializationError_Other',
                /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
                 * Justification: Worker infrastructure code has no mailbox context - runs before user data is established. */
                INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
                errorWithDetails,
                {
                    diagnostic: errorWithDetails?.diagnosticInfo,
                }
            );
        }
    }

    // Reset to default limit
    if (Error.stackTraceLimit) {
        Error.stackTraceLimit = STACK_SIZE_LIMIT;
    }
}

function retryForAnalytics(
    error: Error | undefined,
    _this: MessagePort | Worker,
    pPostMessage: MessagePort['postMessage'],
    message: any,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    options?: any,
    workerName?: ExtendedWorkerOptions['name']
): {
    retried: boolean;
    retriedFailed: boolean;
} {
    let retried = false;
    let retriedFailed = false;

    /**
     * We only retry for the Analytics Web Worker because we do not
     * know what format is expected by other workers.
     */
    if (workerName === 'AnalyticsWorker') {
        // Try calling post message again with the serialized message.
        try {
            retried = true;
            message = serialize(message);
            pPostMessage.call(_this, message, options);
        } catch (caughtRetry) {
            retriedFailed = true;
            const retryError = caughtRetry instanceof Error ? caughtRetry : undefined;

            // Only log an issue when we get two different cloning errors.
            if (error?.message !== retryError?.message) {
                logStartUsage(
                    'SerializationRetryFailed',
                    /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
                     * Justification: Worker infrastructure code has no mailbox context - runs before user data is established. */
                    INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
                    {
                        error1: error?.message,
                        error2: retryError?.message,
                        attempt1: error?.stack?.slice(0, 1024),
                        attempt2: retryError?.stack?.slice(0, 1024),
                    }
                );
            }
        }
    }

    return {
        retried,
        retriedFailed,
    };
}

function isDataCloneError(error: Error | undefined) {
    return (
        error?.name === 'DataCloneError' ||
        error?.message?.includes("Failed to execute 'postMessage") ||
        error?.message?.includes('Data cannot be cloned')
    );
}

export function findAllSerializationErrors(value: any): SerializationIssue[] {
    let issues: SerializationIssue[] = [];

    // Check for known serialization issues
    if (typeof value === 'object') {
        issues = traverseObjectDeep<SerializationIssue>(value, checkKnowSerializationIssue);
    }

    return issues;
}

function checkKnowSerializationIssue(
    parent: any,
    key: string | number,
    path: string
): SerializationIssue | null {
    const issue = getSerializationIssue(parent, key);

    if (issue) {
        issue.key = key;
        issue.path = path;
    }

    return issue;
}
